// frontend/src/components/CountryQRPayment.jsx - Vietnam + China Version
import React, { useState, useEffect, useRef } from 'react';
import { QrCode, Copy, Check, MessageCircle, ArrowLeft, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';

const TELEGRAM_ADMIN = process.env.REACT_APP_TELEGRAM_ADMIN_USERNAME || 'intcounters';

const CountryQRPayment = ({ selectedPlan, onBack, onSuccess }) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [pollStatus, setPollStatus] = useState('waiting'); // waiting | approved | rejected
  const pollRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const countries = {
    vietnam: {
      name: 'Vietnam',
      flag: '🇻🇳',
      currency: 'VND',
      symbol: '₫',
      methods: {
        techcombank: {
          name: 'Techcombank',
          icon: '🏦',
          color: 'from-red-600 to-red-700',
          account: process.env.REACT_APP_TECHCOMBANK_ACCOUNT || 'Techcombank:',
          qrData: '',
          qrImage: '/QRpng/Techcom.png'
        }
      }
    },
    china: {
      name: 'China',
      flag: '🇨🇳',
      currency: 'CNY',
      symbol: '¥',
      methods: {
        wechat: {
          name: 'WeChat Pay',
          icon: '💬',
          color: 'from-green-500 to-green-600',
          account: process.env.REACT_APP_WECHAT_ACCOUNT || 'WeChat:',
          qrData: '',
          qrImage: '/QRpng/weChat.png'
        }
      }
    }
  };

  // Plan prices
  const planPrices = {
    vietnam: {
      'Basic': { amount: 300000, display: '300,000₫' },
      'Pro': { amount: 625000, display: '625,000₫' },
      'Studio': { amount: 1250000, display: '1,250,000₫' }
    },
    china: {
      'Basic': { amount: 85, display: '¥85' },
      'Pro': { amount: 180, display: '¥180' },
      'Studio': { amount: 360, display: '¥360' }
    }
  };

  const normalizePlanName = (planName) => {
    if (!planName) return 'Basic';
    const cleaned = planName.replace(/\s*plan\s*$/i, '').trim();
    const normalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    const validPlans = ['Basic', 'Pro', 'Studio'];
    return validPlans.includes(normalized) ? normalized : 'Basic';
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getCurrentPrice = () => {
    if (!selectedCountry || !selectedPlan) return null;
    const normalizedPlan = normalizePlanName(selectedPlan.name);
    return planPrices[selectedCountry]?.[normalizedPlan];
  };

  const getTelegramUrl = () => {
    const country = countries[selectedCountry];
    const method = country?.methods?.[selectedMethod];
    const price = getCurrentPrice();

    if (!country || !method || !price) return '#';

    const normalizedPlan = normalizePlanName(selectedPlan.name);

    const message = `Hi! I want to upgrade to ${normalizedPlan} Plan (${price.display}) using ${method.name} in ${country.name}.

I have completed the payment and I'm sending you the payment screenshot for verification.

Plan: ${normalizedPlan} Plan
Amount: ${price.display}
Payment Method: ${method.name}
Account: ${method.account}

Please verify and activate my subscription. Thank you!`;

    return `https://t.me/${process.env.telegramUsername || 'intcounters'}?text=${encodeURIComponent(message)}`;
  };

  const startPolling = (pid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.getPaymentStatus(pid);
        if (!res.success) return;
        const s = res.data.status;
        if (s === 'approved') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus('approved');
          setTimeout(() => onSuccess(), 2000);
        } else if (s === 'rejected') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus('rejected');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);
    setTimeout(() => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 30 * 60 * 1000);
  };

  const handlePaymentSubmission = async () => {
    try {
      const country = countries[selectedCountry];
      const price = getCurrentPrice();
      const normalizedPlan = normalizePlanName(selectedPlan.name);

      const result = await api.createCountryQRPayment({
        planName: normalizedPlan,
        amount: price?.amount || 15,
        currency: country?.currency || 'USD',
        paymentMethod: selectedMethod,
        country: selectedCountry,
      });

      if (result.success) {
        const pid = result.data.paymentId || result.data.id;
        await api.iPaid(pid);
        setPaymentId(pid);
        setPaymentSubmitted(true);
        startPolling(pid);
      } else {
        throw new Error(result.error || 'Failed to create payment record');
      }
    } catch (error) {
      console.error('❌ Payment creation failed:', error);
      alert(`Payment creation failed: ${error.message}\n\nPlease try again or contact support.`);
    }
  };

  // Waiting for admin approval (polling screen)
  if (paymentSubmitted) {
    if (pollStatus === 'approved') {
      return (
        <div className="min-h-[600px] bg-gray-900 p-6 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-green-400 mb-4">Payment Approved!</h2>
            <p className="text-gray-300 mb-6">
              Your {normalizePlanName(selectedPlan.name)} plan has been activated. Redirecting to dashboard...
            </p>
          </div>
        </div>
      );
    }

    if (pollStatus === 'rejected') {
      return (
        <div className="min-h-[600px] bg-gray-900 p-6 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">Payment Rejected</h2>
            <p className="text-gray-300 mb-6">
              Your payment could not be verified. Please contact support or try again.
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={() => { setPaymentSubmitted(false); setPollStatus('waiting'); setPaymentId(null); }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                Try Again
              </button>
              <a
                href={`https://t.me/${TELEGRAM_ADMIN}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-medium transition-colors inline-flex items-center"
              >
                Contact Admin
              </a>
            </div>
          </div>
        </div>
      );
    }

    // Default: waiting
    return (
      <div className="min-h-[600px] bg-gray-900 p-6 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">⏳ Waiting for admin approval...</h2>
          <p className="text-gray-400 mb-1">This usually takes 1-5 minutes</p>
          <p className="text-gray-500 text-sm mb-6">
            Your payment has been submitted and the admin has been notified.
          </p>
          <div className="w-10 h-10 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-6"></div>
          <a
            href={`https://t.me/${TELEGRAM_ADMIN}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
          >
            <MessageCircle size={18} className="mr-2" />
            Message admin on Telegram for faster processing
          </a>
        </div>
      </div>
    );
  }

  // Country Selection Screen
  if (!selectedCountry) {
    return (
      <div className="min-h-[600px] bg-gray-900 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <QrCode className="w-8 h-8 text-red-500 mr-3" />
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('regionalQRPayment')}
              </h2>
            </div>
            <p className="text-gray-300">{t('chooseYourCountry')}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 mb-8">
            <div className="text-center">
              <h3 className="text-white font-bold text-lg">{selectedPlan.name}</h3>
              <p className="text-gray-400 text-sm">{t('selectedPlan')}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {Object.entries(countries).map(([countryKey, country]) => {
              const normalizedPlan = normalizePlanName(selectedPlan.name);
              const pricing = planPrices[countryKey]?.[normalizedPlan];
              
              return (
                <div
                  key={countryKey}
                  onClick={() => setSelectedCountry(countryKey)}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-6 cursor-pointer hover:border-red-500 hover:bg-gray-750 transition-all group"
                >
                  <div className="text-center">
                    <div className="text-5xl mb-4">{country.flag}</div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-400">
                      {country.name}
                    </h3>
                    <p className="text-gray-400 mb-4">
                      {t('payIn')} {country.currency}
                    </p>
                    <div className="bg-gray-700 rounded-lg p-3 mb-4">
                      <div className="text-sm text-gray-400">{normalizedPlan} Plan {t('payIn')}</div>
                      <div className="text-xl font-bold text-white">
                        {pricing?.display || 'N/A'}
                      </div>
                    </div>
                    <div className="flex justify-center space-x-2">
                      {Object.values(country.methods).map((method, idx) => (
                        <div key={idx} className="flex items-center space-x-1 text-sm text-gray-400">
                          <span>{method.icon}</span>
                          <span>{method.name.split(' ')[0]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={onBack}
            className="w-full py-3 px-4 bg-gray-800 hover:bg-gray-700 border border-gray-600 hover:border-gray-500 text-white rounded-lg transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // Method Selection Screen
  if (!selectedMethod) {
    const country = countries[selectedCountry];
    const price = getCurrentPrice();
    const methodEntries = Object.entries(country.methods);

    // If only one method, auto-select it
    if (methodEntries.length === 1) {
      setSelectedMethod(methodEntries[0][0]);
      return null;
    }

    return (
      <div className="min-h-[600px] bg-gray-900 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center mb-8">
            <button
              onClick={() => setSelectedCountry(null)}
              className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center">
              <span className="text-3xl mr-3">{country.flag}</span>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {t('payIn')} {country.name}
                </h2>
                <p className="text-gray-400">
                  {normalizePlanName(selectedPlan.name)} Plan - {price?.display}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            {methodEntries.map(([methodKey, method]) => (
              <div
                key={methodKey}
                onClick={() => setSelectedMethod(methodKey)}
                className="bg-gray-800 border border-gray-700 rounded-xl p-6 cursor-pointer hover:border-red-500 hover:bg-gray-750 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${method.color} text-white text-2xl`}>
                      {method.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white group-hover:text-red-400">
                        {method.name}
                      </h3>
                      <p className="text-gray-400">
                        {t('scanWith')} {method.name}
                      </p>
                      <div className="flex items-center mt-1">
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-900/30 text-blue-400">
                          📱 {t('manualVerificationRequired')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-white">→</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Payment Screen
  const country = countries[selectedCountry];
  const method = country?.methods?.[selectedMethod];
  const price = getCurrentPrice();
  const normalizedPlan = normalizePlanName(selectedPlan.name);

  return (
    <div className="min-h-[600px] bg-gray-900 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center mb-8">
          <button
            onClick={() => setSelectedMethod(null)}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center">
            <div className={`p-3 rounded-xl bg-gradient-to-r ${method?.color} text-white text-2xl mr-4`}>
              {method?.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {method?.name} {t('payment')}
              </h2>
              <p className="text-gray-400">
                {country?.name} • {normalizedPlan} Plan • {price?.display}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-red-500/20 rounded-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              {method?.qrImage ? (
                <div className="bg-white p-3 rounded-xl inline-block mb-4">
                  <img
                    src={method.qrImage}
                    alt={`${method.name} QR Code`}
                    className="w-40 h-40 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{ display: 'none' }} className="w-40 h-40 flex items-center justify-center text-gray-600 text-sm">
                    QR Code<br/>Not Available
                  </div>
                </div>
              ) : (
                <div className="bg-gray-200 p-3 rounded-xl inline-block mb-4">
                  <div className="w-40 h-40 flex items-center justify-center text-gray-600 text-sm">
                    QR Code<br/>Not Available
                  </div>
                </div>
              )}
              <p className="text-gray-300 text-sm">
                {t('scanWith')} {method?.name}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  {t('amountToPay')}
                </label>
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-700 px-4 py-3 rounded-lg flex-1">
                    <div className="text-2xl font-bold text-green-400">
                      {price?.display}
                    </div>
                    <div className="text-xs text-gray-400">
                      {country?.currency} • {normalizedPlan} Plan
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(price?.amount?.toString() || '', 'amount')}
                    className="p-3 text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    {copiedField === 'amount' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  {t('paymentAccount')}
                </label>
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-700 px-4 py-3 rounded-lg flex-1 text-white text-sm">
                    {method?.account}
                  </div>
                  <button
                    onClick={() => copyToClipboard(method?.account || '', 'account')}
                    className="p-3 text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    {copiedField === 'account' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handlePaymentSubmission}
          className="w-full py-4 px-6 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl transition-colors mb-6"
        >
          {t('iHavePaid')}
        </button>
      </div>
    </div>
  );
};

export default CountryQRPayment;